import { CompanySettingsTypeID } from '@setvi/shared/services/react-query/query/user/types';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useRealmContext } from 'Providers/MongoRealmProvider/context';
import { MONGO_REALM_COLLECTIONS } from 'Services/MongoRealm';
import { Customer } from 'Services/MongoRealm/Types';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

interface useCustomersProps {
  limit?: number;
}

export const useCustomers = ({ limit = 10 }: useCustomersProps = {}) => {
  const { companyData } = useAppContext();
  const { client } = useRealmContext();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const [skipValue, setSkipValue] = useState<number>(0);
  const { enqueueSnackbar } = useSnackbar();

  const searchCustomers = useCallback(
    async (query: string, skip = 0) => {
      setIsLoading(true);
      setSearchQuery(query);
      try {
        const response: Customer[] = await client
          .db(
            companyData.Settings[CompanySettingsTypeID.AtlasMongoProductDBName]
              ?.Value
          )
          .collection(MONGO_REALM_COLLECTIONS.ACCOUNTS)
          .aggregate([
            {
              $match: {
                $or: [
                  { name: { $regex: query, $options: 'i' } },
                  { accountNumber: { $regex: query, $options: 'i' } },
                  { shippingCity: { $regex: query, $options: 'i' } },
                  { shippingState: { $regex: query, $options: 'i' } },
                  { 'parentAccount.name': { $regex: query, $options: 'i' } },
                  {
                    'parentAccount.accountNumber': {
                      $regex: query,
                      $options: 'i'
                    }
                  }
                ],
                $and: [
                  {
                    catalogSearch: true
                  }
                ]
              }
            },
            { $skip: skip },
            {
              $limit: limit
            }
          ]);

        setCustomers(prevCustomers => [...prevCustomers, ...response]);
        setIsLastPage(response?.length < limit);
        setSkipValue(prevSkip => prevSkip + limit);

        return response;
      } catch (e) {
        enqueueSnackbar('Error occured while fetching customer data', {
          variant: 'error'
        });
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [client, enqueueSnackbar, limit, companyData]
  );

  const getCustomerByAccountNumber = useCallback(
    async (accountNumber: string, skip = 0) => {
      setIsLoading(true);
      setSearchQuery(accountNumber);
      try {
        const response: Customer[] = await client
          .db(
            companyData.Settings[CompanySettingsTypeID.AtlasMongoProductDBName]
              ?.Value
          )
          .collection(MONGO_REALM_COLLECTIONS.ACCOUNTS)
          .aggregate([
            {
              $match: {
                catalogSearch: true,
                accountNumber
              }
            },
            { $skip: skip },
            { $limit: limit }
          ]);

        setCustomers(prev => [...prev, ...response]);
        setIsLastPage(response?.length < limit);
        setSkipValue(prev => prev + limit);

        return response;
      } catch (e) {
        enqueueSnackbar('Error occurred while fetching customer', {
          variant: 'error'
        });
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [client, enqueueSnackbar, limit, companyData]
  );

  const fetchMoreSearchResults = () => {
    if (isLastPage) return;
    searchCustomers(searchQuery, skipValue);
  };

  useEffect(() => {
    setSkipValue(0);
    setCustomers([]);
  }, [searchQuery]);

  return {
    customers,
    isLoading,
    searchCustomers,
    getCustomerByAccountNumber,
    fetchMoreSearchResults,
    searchQuery
  };
};
